import {
    IconHospital,
    IconMilk,
    IconScale,
    IconSperm,
    IconStethoscope,
    IconSyringe,
} from '../../../common/icons';
import { formatDateOnly } from '../../../common/utils/date';

// Registro único de las 6 features SG (admin-sg.md · B2/B3): mapeo tab↔API, columnas
// del historial y tarjetas "último-X". Es la ÚNICA fuente de columnas/tarjetas — los
// componentes (cow-detail/history-tab) no hardcodean campos. Adaptado del panel v1
// (cowre.dashboard/features/sg/lib/features.js) a los payloads reales de v2:
//   - La FECHA del evento es `event_date` a NIVEL RAÍZ, nunca dentro de `payload`
//     (en columns se modela como el pseudo-campo 'event_date').
//   - `service.toro` es el código crudo (no hay toro_nombre en el payload); B2 resuelve
//     `bull_name` best-effort solo en `last_events.service` (nivel entry, no payload).
//   - `parto`/`prenez`/`vence` son fechas DENTRO del payload — formatear defensivamente.

// Helpers de formato compartidos. fmtDate es defensivo: DATEONLY 'YYYY-MM-DD' via
// formatDateOnly (sin new Date(), decisión 0004); un valor no-fecha vuelve tal cual.
export const has = (v) => !(v === null || v === undefined || v === '');
export const val = (v) => (has(v) ? v : '—');
export const fmtDate = (d) => (has(d) ? formatDateOnly(d) : '—');

// ── Marcadores de celda ──────────────────────────────────────────────────────
// Este módulo es .js (sin JSX): no puede construir elementos React. Las celdas que necesitan
// formato devuelven un OBJETO-MARCADOR que `<SgValue>` (layouts/value.jsx) sabe pintar.
export const badge = (label, tone) => ({ __badge: label, tone });
export const note = (text, title) => ({ __note: text, title });

// Texto plano de un valor o marcador, para atributos `title` (un marcador daría "[object Object]").
export const valueText = (value) => {
    if (value && typeof value === 'object') {
        if (value.__badge !== undefined) return String(value.__badge);
        if (value.__note !== undefined) return value.title || String(value.__note);
    }
    return String(val(value));
};

// Estado de la palpación (`tactos.ESTADO`). En el DBF es una letra; se muestra con su nombre.
//   N → Vacía · P → Preñada · C → Confirmada
// `R` existe en los datos (111 casos) y NO tiene fecha de preñez, igual que `N`, pero SG no
// documenta su significado: se muestra el código crudo en gris antes que inventar una etiqueta.
export const PALPATION_STATES = {
    N: { label: 'Vacía',      tone: 'bad'  },
    P: { label: 'Preñada',    tone: 'good' },
    C: { label: 'Confirmada', tone: 'good' },
};

export const palpationBadge = (payload) => {
    const raw = payload?.estado;
    if (!has(raw)) return '—';
    const code = String(raw).trim().toUpperCase();
    const st = PALPATION_STATES[code];
    return st ? badge(st.label, st.tone) : badge(code, 'neutral');
};

// Quién palpó / confirmó. El DBF guarda un CÓDIGO local a la finca (`CO`, `JPS`); el backend lo
// resuelve contra `palpadores.dbf` y adjunta `palpo_name`/`confirmo_name`.
//   - resuelto en su finca      → el nombre
//   - resuelto en OTRA finca    → el nombre + «(?)»: el mismo código puede ser otra persona
//                                 (`CO` es CRISTIAN OSPINA en SI-BN pero CARLOS OSORIO en 01-MAR)
//   - sin catálogo              → el código crudo, nunca un nombre inventado
export const palpadorLabel = (payload, key) => {
    const p = payload || {};
    const name = p[`${key}_name`];
    const code = p[key];
    if (!has(name)) return has(code) ? String(code) : '—';
    return p[`${key}_approx`]
        ? note(`${name} (?)`, `Nombre tomado del catálogo de OTRA finca — el código «${code}» no está dado de alta en la finca de este evento y podría corresponder a otra persona.`)
        : String(name);
};

// Litros del DÍA de un control de leche = SUMA DE ORDEÑOS (am+pm+am2+pm2).
//
// El campo `total` del DBF NO es la producción diaria: SG lo escribe solo en el ÚLTIMO control
// de cada mes y vale `promedio de los controles del mes × días en producción`. Mostrarlo como
// "última leche" le atribuía 171 L a CAÑA DULCE GALÁCTICA, que da 5,5-6,3 L/día (su control del
// 05/05/2025: am=5,54 · total=171,74 = 5,54 × 31 días de mayo).
// Mismo criterio que el backend (services/sg/milk.js). Ver Guia_Controles_Leche_desde_Backups.md §2.
export const milkLiters = (payload) => {
    const p = payload || {};
    let sum = 0;
    let seen = false;
    for (const k of ['am', 'pm', 'am2', 'pm2']) {
        const n = Number(p[k]);
        if (!has(p[k]) || Number.isNaN(n)) continue;
        sum += n; seen = true;
    }
    return seen ? Math.round(sum * 100) / 100 : null;   // null = sin ordeños; 0 = ordeñada sin producción
};

// key    → vocab de `?feature=` del endpoint de eventos.
// columns→ [campo, encabezado, clase?('date'|'mono')]; 'event_date' se lee de la raíz
//          del evento, el resto de `payload`.
// card   → tarjeta "último-X" del Resumen; recibe el ENTRY de last_events[key]
//          ({ event_date, sg_animal_id, payload, bull_name? }).
// chart  → config de la gráfica de tendencia (la UI de la gráfica es D-005).
export const SG_FEATURES = [
    {
        key: 'milk', label: 'Leche', Icon: IconMilk,
        chart: { field: milkLiters, unit: 'L', label: 'Producción de leche (L/día)' },
        // Sin columnas AM2/PM2: el 3.º y 4.º ordeño existen en el DBF pero están vacíos en todo el
        // histórico (0 de 27.668 controles), así que solo robarían ancho. `milkLiters` SÍ los suma,
        // de modo que un hato que los use no subreporta aunque no tengan columna propia.
        columns: [
            ['event_date', 'Fecha', 'date'], [milkLiters, 'Litros/día'],
            ['am', 'AM'], ['pm', 'PM'],
            ['grasa', 'Grasa'], ['prote', 'Prot.'], ['parto', 'Parto', 'date'],
            ['total', 'Cierre mes'],
        ],
        card: {
            title: 'Última leche',
            big: (e) => { const l = milkLiters(e.payload); return l == null ? '—' : `${l} L`; },
            sub: (e) => `Fecha: ${fmtDate(e.event_date)}`,
            rows: (e) => {
                const p = e.payload || {};
                // Los ordeños 3 y 4 solo se listan si el hato los usa (la mayoría ordeña 1-2 veces).
                const extra = has(p.am2) || has(p.pm2) ? [['AM2 / PM2', `${val(p.am2)} / ${val(p.pm2)}`]] : [];
                return [
                    ['AM / PM', `${val(p.am)} / ${val(p.pm)}`],
                    ...extra,
                    ['Grasa / Prot.', `${val(p.grasa)} / ${val(p.prote)}`],
                    ['Parto', fmtDate(p.parto)],
                    ['Cierre del mes', has(p.total) && Number(p.total) > 0 ? `${p.total} L` : '—'],
                ];
            },
        },
    },
    {
        key: 'service', label: 'Servicios', Icon: IconSperm,
        columns: [
            ['event_date', 'Fecha', 'date'], ['toro', 'Toro', 'mono'], ['tipo', 'Tipo'],
            ['insemina', 'Inseminó'], ['embrion', 'Embrión'],
        ],
        card: {
            title: 'Último servicio y toro',
            big: (e) => val(e.bull_name || e.payload?.toro),
            sub: (e) => `${fmtDate(e.event_date)} · ${val(e.payload?.tipo)}`,
            rows: (e) => {
                const p = e.payload || {};
                return [
                    ['Toro (código)', val(p.toro), 'mono'],
                    ['Inseminó', val(p.insemina)],
                    ['Embrión', val(p.embrion)],
                ];
            },
        },
    },
    {
        key: 'calving', label: 'Partos', Icon: IconHospital,
        columns: [
            ['event_date', 'Fecha', 'date'], ['nro', 'Parto #'], ['cria', 'Cría', 'mono'],
            ['hij2', 'Gemelo'], ['pesnac', 'Peso nac.'],
        ],
        card: {
            title: 'Último parto y cría',
            big: (e) => (has(e.payload?.nro) ? `Parto #${e.payload.nro}` : '—'),
            sub: (e) => fmtDate(e.event_date),
            rows: (e) => {
                const p = e.payload || {};
                return [
                    ['Cría (registro)', val(p.cria), 'mono'],
                    ['Gemelo', val(p.hij2)],
                    ['Peso nac.', val(p.pesnac)],
                ];
            },
        },
    },
    {
        key: 'palpation', label: 'Palpaciones', Icon: IconStethoscope,
        columns: [
            ['event_date', 'Fecha', 'date'], [palpationBadge, 'Estado'], ['prenez', 'Preñez', 'date'],
            [(p) => palpadorLabel(p, 'palpo'), 'Palpó'],
            [(p) => palpadorLabel(p, 'confirmo'), 'Confirmó'],
        ],
        card: {
            title: 'Última palpación (dx)',
            big: (e) => fmtDate(e.event_date),
            sub: (e) => palpationBadge(e.payload),
            rows: (e) => {
                const p = e.payload || {};
                return [
                    ['Preñez', fmtDate(p.prenez)],
                    ['Palpó', palpadorLabel(p, 'palpo')],
                    ['Confirmó', palpadorLabel(p, 'confirmo')],
                ];
            },
        },
    },
    {
        key: 'weight', label: 'Pesos', Icon: IconScale,
        chart: { field: 'peso', unit: 'kg', label: 'Peso (kg)' },
        columns: [
            ['event_date', 'Fecha', 'date'], ['peso', 'Peso'], ['alzada', 'Alzada'], ['evento', 'Evento'],
        ],
        card: {
            title: 'Último peso',
            big: (e) => (has(e.payload?.peso) ? `${e.payload.peso} kg` : '—'),
            sub: (e) => fmtDate(e.event_date),
            rows: (e) => {
                const p = e.payload || {};
                return [
                    ['Alzada', val(p.alzada)],
                    ['Evento', val(p.evento)],
                ];
            },
        },
    },
    {
        key: 'treatment', label: 'Tratamientos', Icon: IconSyringe,
        columns: [
            ['event_date', 'Fecha', 'date'], ['tipo1', 'Tipo'], ['tipo2', 'Tipo 2'],
            ['producto', 'Producto'], ['cantidad', 'Cant.'], ['vence', 'Vence', 'date'],
        ],
        card: {
            title: 'Último tratamiento',
            big: (e) => val(e.payload?.producto || e.payload?.tipo1),
            sub: (e) => fmtDate(e.event_date),
            rows: (e) => {
                const p = e.payload || {};
                return [
                    ['Tipo', [p.tipo1, p.tipo2, p.tipo3].filter(Boolean).join(' · ') || '—'],
                    ['Cantidad', val(p.cantidad)],
                    ['Vence', fmtDate(p.vence)],
                ];
            },
        },
    },
];

export const SG_FEATURE_BY_KEY = Object.fromEntries(SG_FEATURES.map((f) => [f.key, f]));
